import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { Chip } from "../Chip";
import { fmtActor, fmtDate } from "../../format";
import {
  CONDITION_CONFIRM_DISCLOSURE,
  CONDITION_REMOVE_DISCLOSURE,
  CONDITION_REVIEW_FLAG_DISPLAY,
  CONDITION_REVIEW_FLAG_EXPLANATION,
} from "../../vocabulary";
import type { ConditionRow } from "../../types";

/**
 * The other two ends of an accepted condition's life: **remove** it, and
 * **re-confirm** it after an automated event flagged it for a human re-read.
 *
 * One component for both because the load-bearing half is identical — what an
 * operator needs in front of them is the condition itself: its scope, who
 * accepted it and when, the reason in their words, and (structural only) the
 * exit condition that was supposed to decide this moment. Removing a standing
 * disposition without re-reading the prose that justified it is exactly the
 * mistake the exit condition exists to prevent, so the prose is not behind a
 * link.
 *
 * Validation follows the API: removal demands a reason, confirmation takes an
 * optional note. Neither is auto-retired — a human is the only exit, which is
 * why both ends are logged symmetrically into `tracking_admin_event`.
 */
export function ConditionActionModal({
  condition,
  action,
  onSubmit,
  onClose,
  pending,
}: {
  condition: ConditionRow;
  action: "remove" | "confirm";
  onSubmit: (text: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [text, setText] = useState("");
  const removing = action === "remove";
  const scopeLabel =
    condition.scopeType === "rule"
      ? `Rule ${condition.scopeId}`
      : `Problem ${condition.scopeId}`;

  return (
    <ActionModal
      title={`${removing ? "Remove" : "Re-confirm"} accepted condition — ${scopeLabel}`}
      submitLabel={removing ? "Remove condition" : "Confirm it still stands"}
      onSubmit={() => onSubmit(text.trim())}
      onClose={onClose}
      pending={pending}
      danger={removing}
      disabled={removing && !text.trim()}
    >
      {/* The condition, in full — see also the doc comment: the prose that
          justified it is the thing being acted on, so it leads. */}
      <div className="rounded-md border border-[#E9E9EB] bg-[#FAFAFB] p-3 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Chip
            tone={condition.reasonCategory === "commercial" ? "blue" : "purple"}
          >
            {condition.reasonCategory}
          </Chip>
          <span className="text-xs text-gray-500">
            accepted {fmtDate(condition.appliedAt)} by{" "}
            {fmtActor(condition.appliedBy)} ·{" "}
            {condition.ageDays}d ago
          </span>
        </div>
        <p className="text-sm text-content-primary">{condition.reason}</p>
        {condition.exitCondition && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">Exit condition:</span>{" "}
            {condition.exitCondition}
          </p>
        )}
        {condition.lastConfirmedAt && (
          <p className="text-xs text-gray-400">
            last re-confirmed {fmtDate(condition.lastConfirmedAt)}
            {condition.lastConfirmedBy &&
              ` by ${fmtActor(condition.lastConfirmedBy)}`}
          </p>
        )}
      </div>

      {condition.reviewFlag && (
        <div className="rounded-md border border-warning-light bg-warning-light/40 p-3">
          <div className="flex items-center gap-2 mb-1">
            <Chip tone="orange">re-review</Chip>
            <span className="text-xs font-medium text-content-primary">
              {CONDITION_REVIEW_FLAG_DISPLAY[condition.reviewFlag]}
              {condition.reviewFlaggedAt &&
                ` · flagged ${fmtDate(condition.reviewFlaggedAt)}`}
            </span>
          </div>
          <p className="text-xs text-gray-600">
            {CONDITION_REVIEW_FLAG_EXPLANATION[condition.reviewFlag]}
          </p>
        </div>
      )}

      <div>
        <label className={FIELD_LABEL_CLASS}>
          {removing ? "Reason (required)" : "Note (optional)"}
        </label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            removing
              ? "What changed — the exit condition was met, the arrangement ended, or this was accepted in error"
              : "What you re-read, and why it still stands"
          }
          autoFocus
        />
      </div>

      <p className="text-xs text-gray-400">
        {removing ? CONDITION_REMOVE_DISCLOSURE : CONDITION_CONFIRM_DISCLOSURE}
      </p>
    </ActionModal>
  );
}
